import { Overlay } from "components/common/Overlay";
import { Select } from "components/common/Select";
import { ParameterSetDto, ParameterSetNameDto, ScenarioDto } from "dtos/dtos";
import { useEffect } from "react";
import { ReactElement, useState } from "react";
import { Action, State } from "../../state/state";

export function CalculationHead(props: {
  dispatch: (action: Action) => void;
  state: State;
}): ReactElement {
  const dispatch = props.dispatch;
  const state = props.state;
  const scenarioNames = state.zoningPlanDraft.scenarioNames;
  const parameterSetNamesLeft = state.zoningPlanDraft.parameterSetNamesLeft;
  const parameterSetNamesRight = state.zoningPlanDraft.parameterSetNamesRight;
  const selectedScenarioLeft = state.zoningPlanDraft.selectedScenarioLeft;
  const selectedScenarioRight = state.zoningPlanDraft.selectedScenarioRight;
  const selectedParameterSetLeft =
    state.zoningPlanDraft.selectedParameterSetLeft;
  const selectedParameterSetRight =
    state.zoningPlanDraft.selectedParameterSetRight;

  const [loading, setLoading] = useState<boolean>(false);
  const [loadScenarioIdLeft, setLoadScenarioIdLeft] = useState<string>();
  const [loadParameterSetIdLeft, setLoadParameterSetIdLeft] =
    useState<string>();
  const [loadScenarioIdRight, setLoadScenarioIdRight] = useState<string>();
  const [loadParameterSetIdRight, setLoadParameterSetIdRight] =
    useState<string>();

  async function loadScenario(
    id: string
  ): Promise<
    | { scenario: ScenarioDto; parameterSetNames: ParameterSetNameDto[] }
    | undefined
  > {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/scenarios/id/${id}`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const scenario: ScenarioDto = await response.json();

        const parameterSetNamesResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/parametersetnames/${scenario.id}`,
          {
            credentials: "include",
          }
        );

        if (parameterSetNamesResponse.ok) {
          const parameterSetNames: ParameterSetNameDto[] =
            await parameterSetNamesResponse.json();
          return { scenario: scenario, parameterSetNames: parameterSetNames };
        }

        return undefined;
      }

      return undefined;
    } catch {}
  }

  async function loadParameterSet(
    id: string
  ): Promise<ParameterSetDto | undefined> {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/parametersets/id/${id}`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const parameterSet: ParameterSetDto = await response.json();
        return parameterSet;
      }
      return undefined;
    } catch {}
  }

  useEffect(() => {
    async function load(scenarioId: string): Promise<void> {
      setLoading(true);
      const result = await loadScenario(scenarioId);
      dispatch({
        type: "setSelectedScenarioLeft",
        data: {
          scenario: result?.scenario,
          parameterSetNames: result?.parameterSetNames,
        },
      });
      setLoading(false);
    }

    if (loadScenarioIdLeft !== undefined) {
      load(loadScenarioIdLeft);
    }
  }, [loadScenarioIdLeft, dispatch]);

  useEffect(() => {
    async function load(scenarioId: string): Promise<void> {
      setLoading(true);
      const result = await loadScenario(scenarioId);
      dispatch({
        type: "setSelectedScenarioRight",
        data: {
          scenario: result?.scenario,
          parameterSetNames: result?.parameterSetNames,
        },
      });
      setLoading(false);
    }

    if (loadScenarioIdRight !== undefined && loadScenarioIdRight !== "") {
      load(loadScenarioIdRight);
    } else if (loadScenarioIdRight === "") {
      dispatch({
        type: "setSelectedScenarioRight",
        data: {
          scenario: undefined,
        },
      });
    }
  }, [loadScenarioIdRight, dispatch]);

  useEffect(() => {
    async function load(parameterSetId: string) {
      const parameterSet = await loadParameterSet(parameterSetId);
      dispatch({
        type: "setSelectedParameterSetLeft",
        data: { parameterSet: parameterSet },
      });
      setLoadParameterSetIdLeft(undefined);
    }

    if (loadParameterSetIdLeft !== undefined) {
      load(loadParameterSetIdLeft);
    }
  }, [loadParameterSetIdLeft, dispatch]);

  useEffect(() => {
    async function load(parameterSetId: string) {
      const parameterSet = await loadParameterSet(parameterSetId);
      dispatch({
        type: "setSelectedParameterSetRight",
        data: { parameterSet: parameterSet },
      });
      setLoadParameterSetIdRight(undefined);
    }

    if (loadParameterSetIdRight !== undefined) {
      load(loadParameterSetIdRight);
    }
  }, [loadParameterSetIdRight, dispatch]);

  useEffect(() => {
    if (
      selectedScenarioLeft === undefined &&
      scenarioNames !== undefined &&
      scenarioNames.length === 1
    ) {
      setLoadScenarioIdLeft(scenarioNames[0].id.toString());
    }
  }, [selectedScenarioLeft, scenarioNames]);

  useEffect(() => {
    if (
      selectedParameterSetLeft === undefined &&
      parameterSetNamesLeft !== undefined &&
      parameterSetNamesLeft.length === 1
    ) {
      setLoadParameterSetIdLeft(parameterSetNamesLeft[0].id.toString());
    }
  }, [selectedParameterSetLeft, parameterSetNamesLeft]);

  useEffect(() => {
    if (
      selectedParameterSetRight === undefined &&
      parameterSetNamesRight !== undefined &&
      parameterSetNamesRight.length === 1
    ) {
      setLoadParameterSetIdRight(parameterSetNamesRight[0].id.toString());
    }
  }, [selectedParameterSetRight, parameterSetNamesRight]);

  return (
    <>
      <Overlay show={loading} text="Lade Daten ..." />
      <div className="pb-8"></div>
      <div className="pb-8 flex flex-col gap-4">
        <p className="font-bold">Zonenplan Basis</p>
        <Select
          header={{ displayValue: "Zonenplan auswählen", enabled: false }}
          options={scenarioNames?.map((s) => {
            return {
              value: s.id.toString(),
              displayValue: `${s.name} ${s.status}`,
            };
          })}
          value={selectedScenarioLeft?.id.toString()}
          width="w-auto"
          visible={true}
          onChange={(value) => setLoadScenarioIdLeft(value)}
        />
        <Select
          header={{ displayValue: "Kennzahlen auswählen", enabled: false }}
          options={parameterSetNamesLeft?.map((ps) => {
            return { value: ps.id.toString(), displayValue: ps.name };
          })}
          value={selectedParameterSetLeft?.id.toString()}
          width="w-auto"
          visible={true}
          onChange={(value) => setLoadParameterSetIdLeft(value)}
        />
      </div>
      <div className="pb-8 flex flex-col gap-4">
        <p className="font-bold">Zonenplan Vergleich</p>
        <Select
          header={{ displayValue: "Zonenplan auswählen", enabled: true }}
          options={scenarioNames?.map((s) => {
            return {
              value: s.id.toString(),
              displayValue: `${s.name} ${s.status}`,
            };
          })}
          value={selectedScenarioRight?.id.toString()}
          width="w-auto"
          visible={true}
          onChange={(value) => setLoadScenarioIdRight(value)}
        />
        <Select
          header={{ displayValue: "Kennwerte auswählen", enabled: false }}
          options={parameterSetNamesRight?.map((ps) => {
            return { value: ps.id.toString(), displayValue: ps.name };
          })}
          value={selectedParameterSetRight?.id.toString()}
          width="w-auto"
          visible={true}
          onChange={(value) => setLoadParameterSetIdRight(value)}
        />
      </div>
      <div className="pb-8 font-bold text-center">Differenz</div>
    </>
  );
}
